import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Settings,
  LayoutDashboard,
  Package,
  Users,
  Trash2,
  ExternalLink,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Wifi,
  Clock,
} from 'lucide-react';
import DisconnectModal from '../ui/DisconnectModal';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-2xl flex items-center gap-3 sm:gap-5 shadow-sm">
    <div className={`p-2.5 sm:p-3.5 rounded-xl ${bg} border border-gray-100`}>
      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${color}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-xl sm:text-2xl font-black text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const statusDisplay = (overallStatus) => {
  switch (overallStatus) {
    case 'active':
      return { label: 'Active', color: 'text-green-600', bg: 'bg-green-50', Icon: CheckCircle };
    case 'syncing':
      return { label: 'Syncing', color: 'text-blue-600', bg: 'bg-blue-50', Icon: Loader2 };
    case 'sync_failed':
      return { label: 'Sync failed', color: 'text-red-600', bg: 'bg-red-50', Icon: AlertCircle };
    case 'connected_not_listening':
      return { label: 'No webhooks', color: 'text-amber-600', bg: 'bg-amber-50', Icon: AlertCircle };
    case 'disconnected':
      return { label: 'Disconnected', color: 'text-gray-500', bg: 'bg-gray-50', Icon: AlertCircle };
    default:
      return {
        label: overallStatus?.replace(/_/g, ' ') || '—',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        Icon: AlertCircle,
      };
  }
};

const WooCommerceIntegrationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
  });
  const [productsLoading, setProductsLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customersMeta, setCustomersMeta] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
  });
  const [customersLoading, setCustomersLoading] = useState(false);

  const pollRef = useRef(null);

  useEffect(() => {
    if (!id) navigate('/integrations/woocommerce');
  }, [id, navigate]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getWooCommerceStatus();
      const found = (data?.accounts || []).find((a) => a.integration_id === id);
      if (!found) {
        setError('Integration not found.');
        setAccount(null);
      } else {
        setAccount(found);
        if (found.sync?.status === 'syncing') setSyncing(true);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load integration details.');
      toast.error('Could not load WooCommerce details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (!syncing || !id) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await ApiService.getWooCommerceSyncStatus(id);
        const sync = status?.sync || {};
        if (sync.status === 'syncing') {
          pollRef.current = setTimeout(poll, 2500);
        } else {
          setSyncing(false);
          await fetchStatus();
          if (sync.status === 'failed') {
            toast.error('Sync failed — check logs.');
          } else {
            toast.success('Sync complete.');
          }
        }
      } catch {
        setSyncing(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [syncing, id]);

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      const res = await ApiService.triggerWooCommerceSync(id);
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to start sync');
      }
      toast.success('Sync started');
    } catch (err) {
      setSyncing(false);
      toast.error(err.message || 'Failed to start sync');
    }
  };

  const handleDisconnect = async ({ purgeContent = false } = {}) => {
    try {
      setIsDisconnecting(true);
      const response = await ApiService.disconnectWooCommerce(id, { purgeContent });
      if (response?.success) {
        toast.success(
          purgeContent
            ? 'Store disconnected and product content purged.'
            : 'WooCommerce store disconnected.'
        );
        navigate('/integrations/woocommerce');
      } else {
        throw new Error(response?.message || 'Failed to disconnect.');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to disconnect WooCommerce.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const loadProducts = async (page = 1) => {
    try {
      setProductsLoading(true);
      const data = await ApiService.getWooCommerceProducts(id, { page, size: 20 });
      if (data?.success) {
        setProducts(data.data || []);
        setProductsMeta(data.metadata || { current_page: page, total_pages: 1, total_items: 0 });
      }
    } catch {
      toast.error('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  const loadCustomers = async (page = 1) => {
    try {
      setCustomersLoading(true);
      const data = await ApiService.getWooCommerceCustomers(id, { page, size: 20 });
      if (data?.success) {
        setCustomers(data.data || []);
        setCustomersMeta(data.metadata || { current_page: page, total_pages: 1, total_items: 0 });
      }
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'products' && id) loadProducts(1);
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'customers' && id) loadCustomers(1);
  }, [activeTab, id]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="h-10 w-10 animate-spin text-black mb-4" />
        <p className="text-gray-500 font-medium">Loading integration…</p>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">{error || 'Integration not found'}</h2>
        <button
          onClick={() => navigate('/integrations/woocommerce')}
          className="mt-4 px-5 py-2.5 bg-black text-white font-bold rounded-xl"
        >
          Back to WooCommerce
        </button>
      </div>
    );
  }

  const storeUrl = account.store_url || '';
  const displayUrl = storeUrl.replace(/^https?:\/\//, '');
  const st = statusDisplay(account.overall_status);
  const StatusIcon = st.Icon;

  return (
    <div className="p-6 sm:p-8">
      <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button
          onClick={() => navigate('/integrations/woocommerce')}
          className="p-2 hover:bg-gray-100 rounded-xl"
        >
          <ArrowLeft className="h-6 w-6 text-gray-700" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <img
              src="https://cdn.simpleicons.org/woocommerce/96588A"
              alt="WooCommerce"
              className="w-8 h-8"
            />
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight truncate">
              {displayUrl}
            </h1>
          </div>
          <p className="text-gray-500 font-medium mt-1 text-sm">WooCommerce integration</p>
        </div>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50"
        >
          Open store <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="flex gap-2 mb-8 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${
              activeTab === t.id
                ? 'bg-black text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatCard
              label="Status"
              value={st.label}
              icon={StatusIcon}
              color={st.color}
              bg={st.bg}
            />
            <StatCard
              label="Products synced"
              value={account.sync?.products_synced ?? 0}
              icon={Package}
              color="text-[#96588A]"
              bg="bg-[#96588A]/10"
            />
            <StatCard
              label="Customers synced"
              value={account.sync?.customers_synced ?? 0}
              icon={Users}
              color="text-blue-600"
              bg="bg-blue-50"
            />
            <StatCard
              label="Last product sync"
              value={
                account.sync?.last_synced_at
                  ? formatDate(account.sync.last_synced_at).split(',')[0]
                  : 'Never'
              }
              icon={Clock}
              color="text-blue-600"
              bg="bg-blue-50"
            />
            <StatCard
              label="Webhooks"
              value={account.webhook?.is_receiving_events ? 'Receiving' : 'Not listening'}
              icon={Wifi}
              color={account.webhook?.is_receiving_events ? 'text-green-600' : 'text-gray-400'}
              bg={account.webhook?.is_receiving_events ? 'bg-green-50' : 'bg-gray-50'}
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Catalog sync</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Pull the latest products into the knowledge base and refresh customer records.
                  Orders are looked up live during support — they are not bulk-synced.
                </p>
              </div>
              <button
                onClick={handleSyncNow}
                disabled={syncing}
                className="flex items-center justify-center gap-2 px-5 py-3 bg-black text-white font-bold rounded-xl hover:bg-gray-800 disabled:opacity-60"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>
            </div>
            {syncing && (
              <p className="mt-4 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Sync in progress. This may take a few minutes for large catalogs.
              </p>
            )}
          </div>
        </motion.div>
      )}

      {activeTab === 'products' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Synced products</h3>
              <span className="text-xs font-bold text-gray-400">{productsMeta.total_items} total</span>
            </div>
            {productsLoading ? (
              <div className="p-12 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : products.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No products synced yet. Run a sync from the Overview tab.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th className="px-6 py-3">Title</th>
                        <th className="px-6 py-3">External ID</th>
                        <th className="px-6 py-3">Updated</th>
                        <th className="px-6 py-3">Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {products.map((p) => (
                        <tr key={p.content_id || p.external_id} className="hover:bg-gray-50">
                          <td className="px-6 py-3 font-medium text-gray-900 max-w-xs truncate">
                            {p.title || p.metadata?.title || '—'}
                          </td>
                          <td className="px-6 py-3 text-gray-500 font-mono text-xs">
                            {p.external_id || p.metadata?.external_id || '—'}
                          </td>
                          <td className="px-6 py-3 text-gray-500">
                            {p.updated_at ? formatDate(p.updated_at) : '—'}
                          </td>
                          <td className="px-6 py-3">
                            {(p.original_url || p.metadata?.permalink) && (
                              <a
                                href={p.original_url || p.metadata?.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#96588A] hover:underline flex items-center gap-1"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {productsMeta.total_pages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                    <button
                      disabled={productsMeta.current_page <= 1}
                      onClick={() => loadProducts(productsMeta.current_page - 1)}
                      className="px-3 py-1.5 text-sm font-bold border rounded-lg disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">
                      Page {productsMeta.current_page} of {productsMeta.total_pages}
                    </span>
                    <button
                      disabled={productsMeta.current_page >= productsMeta.total_pages}
                      onClick={() => loadProducts(productsMeta.current_page + 1)}
                      className="px-3 py-1.5 text-sm font-bold border rounded-lg disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}

      {activeTab === 'customers' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">Synced customers</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Stored for support context only — not added to the knowledge base
                </p>
              </div>
              <span className="text-xs font-bold text-gray-400">{customersMeta.total_items} total</span>
            </div>
            {customersLoading ? (
              <div className="p-12 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : customers.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No customers synced yet. Run a sync from the Overview tab.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th className="px-6 py-3">Name</th>
                        <th className="px-6 py-3">Email</th>
                        <th className="px-6 py-3">Orders</th>
                        <th className="px-6 py-3">Total spent</th>
                        <th className="px-6 py-3">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {customers.map((c) => (
                        <tr key={c.external_id || c.email} className="hover:bg-gray-50">
                          <td className="px-6 py-3 font-medium text-gray-900">
                            {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.username || '—'}
                          </td>
                          <td className="px-6 py-3 text-gray-600">{c.email || '—'}</td>
                          <td className="px-6 py-3 text-gray-500">{c.orders_count ?? 0}</td>
                          <td className="px-6 py-3 text-gray-500">{c.total_spent ?? '0'}</td>
                          <td className="px-6 py-3 text-gray-500">
                            {c.updated_at ? formatDate(c.updated_at) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {customersMeta.total_pages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                    <button
                      disabled={customersMeta.current_page <= 1}
                      onClick={() => loadCustomers(customersMeta.current_page - 1)}
                      className="px-3 py-1.5 text-sm font-bold border rounded-lg disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">
                      Page {customersMeta.current_page} of {customersMeta.total_pages}
                    </span>
                    <button
                      disabled={customersMeta.current_page >= customersMeta.total_pages}
                      onClick={() => loadCustomers(customersMeta.current_page + 1)}
                      className="px-3 py-1.5 text-sm font-bold border rounded-lg disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}

      {activeTab === 'settings' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Connection</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Store URL</dt>
                <dd className="font-medium text-gray-900 text-right break-all">{storeUrl}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Connected at</dt>
                <dd className="font-medium text-gray-900">{formatDate(account.connected_at)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Integration ID</dt>
                <dd className="font-mono text-xs text-gray-600 text-right break-all">{account.integration_id}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Customers last synced</dt>
                <dd className="font-medium text-gray-900">
                  {account.sync?.customers_last_synced_at
                    ? formatDate(account.sync.customers_last_synced_at)
                    : 'Never'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Registered webhooks</h3>
              {(() => {
                const detailed = account.webhook?.registered_webhooks || [];
                const ids = account.webhook?.registered_webhook_ids || [];
                const count = detailed.length || ids.length;
                return count > 0 ? (
                  <span className="text-xs font-bold text-gray-400">{count} active</span>
                ) : null;
              })()}
            </div>
            {(() => {
              // Preferred: detailed records from backend (id + topic + status)
              const detailed = account.webhook?.registered_webhooks || [];
              // Fallback for older connections that only stored numeric ids
              const FALLBACK_TOPICS = [
                'product.created',
                'product.updated',
                'product.deleted',
                'product.restored',
                'order.created',
                'order.updated',
                'customer.created',
                'customer.updated',
                'customer.deleted',
              ];
              const ids = account.webhook?.registered_webhook_ids || [];
              const rows =
                detailed.length > 0
                  ? detailed
                  : ids.map((wid, i) => ({
                      id: wid,
                      topic: FALLBACK_TOPICS[i] || 'webhook',
                      name: FALLBACK_TOPICS[i] ? `Uttik ${FALLBACK_TOPICS[i]}` : `Webhook #${wid}`,
                      status: 'active',
                    }));

              if (rows.length === 0) {
                return (
                  <p className="text-sm text-gray-500">No webhooks registered (or still pending).</p>
                );
              }

              return (
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th className="px-4 py-2.5">Topic</th>
                        <th className="px-4 py-2.5">Name</th>
                        <th className="px-4 py-2.5">Status</th>
                        <th className="px-4 py-2.5 text-right">ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((wh) => (
                        <tr key={wh.id || wh.topic} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">
                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {wh.topic}
                            </code>
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">{wh.name || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                                (wh.status || 'active') === 'active'
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {wh.status || 'active'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-gray-400">
                            {wh.id}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            <p className="mt-3 text-xs text-gray-400">
              Auto-registered by Uttik on connect. Merchants do not need to add webhooks manually in
              WooCommerce. Order events are acknowledged only — order details are looked up live
              during support.
            </p>
          </div>

          <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-red-800 mb-2">Danger zone</h3>
            <p className="text-sm text-red-600 mb-4">
              Disconnecting clears API keys, unregisters webhooks, and removes synced customer
              records. You can optionally also purge product content from the knowledge base.
            </p>
            <button
              onClick={() => setIsDisconnectModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" /> Disconnect store
            </button>
          </div>
        </motion.div>
      )}

      <DisconnectModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        integrationName="WooCommerce"
        isLoading={isDisconnecting}
        showPurgeOption
        purgeLabel="Also delete synced product content from the knowledge base (vectors + content docs)"
      />
    </div>
  );
};

export default WooCommerceIntegrationDetails;
